import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportButtons } from "./ConferenceShared";

const REPORTS = [
  ["stock_register", "Complete Receipt Stock Register"],
  ["batch_register", "Receipt Batch Register"],
  ["numbering_register", "Book & Receipt Number Register"],
  ["district_allocation", "District-wise Receipt Allocation"],
  ["member_allocation", "Member-wise Receipt Allocation"],
  ["collections", "Member-wise Collection Register"],
  ["district_collections", "District-wise Collection Report"],
  ["targets", "State Contribution Target Report"],
  ["transfers", "State Fund Transfer Report"],
  ["surplus", "District Surplus Report"],
  ["expenditures", "District Expenditure Report"],
  ["unused_receipts", "Unused Receipt Report"],
  ["cancelled_receipts", "Cancelled Receipt Report"],
  ["damaged_books", "Damaged Receipt Book Report"],
  ["performance", "Member Collection Performance"],
  ["final_statement", "Final State Conference Financial Statement"],
  ["reconciliation", "Final Receipt Reconciliation Report"],
];

export default function ConferenceReportsPanel({ districts, categories }) {
  const [report, setReport] = useState("final_statement");
  const [filters, setFilters] = useState({ district: "all", category: "all", status: "", date_from: "", date_to: "", member: "" });
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.district !== "all") params.district = filters.district;
      if (filters.category !== "all") params.category_key = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.member) params.member_id = filters.member;
      const [r, a] = await Promise.all([
        api.get(`/conference/reports/${report}`, { params }),
        api.get("/conference/audit"),
      ]);
      setData(r.data); setAudit(a.data.slice(0, 100));
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [report]);

  const rows = data ? [data.headers, ...data.rows] : [];
  return (
    <div className="grid xl:grid-cols-[1fr_360px] gap-5" data-testid="conference-reports-panel">
      <Card className="p-5">
        <div className="flex justify-between gap-3 flex-wrap mb-4"><div><h3 className="font-black text-lg">Reports / റിപ്പോർട്ടുകൾ</h3><p className="text-sm text-muted-foreground">All reports use live portal data and current filters.</p></div><ExportButtons rows={rows} title={data?.title || "Conference Report"} filename={`kelta_${report}`} disabled={loading} testId="report-export" /></div>
        <div className="grid md:grid-cols-3 xl:grid-cols-6 gap-2 mb-4">
          <div className="xl:col-span-2"><Label>Report</Label><Select value={report} onValueChange={setReport}><SelectTrigger data-testid="report-select"><SelectValue /></SelectTrigger><SelectContent>{REPORTS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>District</Label><Select value={filters.district} onValueChange={v => setFilters(f => ({ ...f, district: v }))}><SelectTrigger data-testid="report-district"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All districts</SelectItem>{districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Category</Label><Select value={filters.category} onValueChange={v => setFilters(f => ({ ...f, category: v }))}><SelectTrigger data-testid="report-category"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>From</Label><Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="report-date-from" /></div>
          <div><Label>To</Label><Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="report-date-to" /></div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="report-refresh">{loading ? "Loading…" : "Apply Filters"}</Button>
        <div className="mt-4 overflow-auto max-h-[620px] rounded-md border border-border" data-testid="report-table">
          {data ? <Table><TableHeader className="sticky top-0 bg-card"><TableRow>{data.headers.map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow></TableHeader><TableBody>{data.rows.map((r, i) => <TableRow key={i} data-testid={`report-row-${i}`}>{r.map((c, j) => <TableCell key={j} className="whitespace-nowrap">{String(c ?? "—")}</TableCell>)}</TableRow>)}</TableBody></Table> : <div className="p-10 text-center text-muted-foreground">Loading report…</div>}
        </div>
      </Card>

      <Card className="p-5" data-testid="conference-audit-card">
        <h3 className="font-black text-lg">Audit Trail / ഓഡിറ്റ്</h3>
        <p className="text-xs text-muted-foreground mb-3">Every stock, numbering, collection and finance action is recorded.</p>
        <div className="space-y-2 max-h-[650px] overflow-auto">{audit.map(a => <div key={a.id} className="rounded-lg border border-border p-3"><div className="text-xs font-bold">{a.action.replaceAll("_", " ")}</div><div className="text-[10px] text-muted-foreground">{new Date(a.at).toLocaleString("en-IN")} · {a.actor_email}</div>{a.district && <div className="text-[10px]">{a.district}</div>}</div>)}</div>
      </Card>
    </div>
  );
}
